import JointAccountOwnerApiClient from "@/api/jointAccountOwnerApiClient";
import { AppToasterV2, dialogStore } from "@ucl/library";
import { useCallback, useEffect, useState } from "react";
import { useAuthenticatedApiClient } from "./useAuthenticatedApiClient";
import { FinancialApplicationFormValidationDisplayBehavior, useFinancialApplicationFormSectionValidation, FinancialApplicationFormSectionContextType } from "./useFinancialApplicationFormSectionValidation";
import { FinancialApplicationSection } from "@/types/enums";
import { useFinancialApplicationForm } from "./useFinancialApplicationForm";

export const useJointAccountOwners = (
  financialApplicationId: string, 
  validationBehavior?: FinancialApplicationFormValidationDisplayBehavior,
  validationContextType?: FinancialApplicationFormSectionContextType
) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [jointAccountOwnerIds, setJointAccountOwnerIds] = useState<string[]>([]);
  const jointAccountOwnerApiClient = useAuthenticatedApiClient(JointAccountOwnerApiClient);

  const { refreshFinancialApplication } = useFinancialApplicationForm(financialApplicationId);

  const { getCollectionValidationErrors, validateFinancialApplicationFormSection } = useFinancialApplicationFormSectionValidation(
    financialApplicationId,
    FinancialApplicationSection.JointAccountOwners,
    validationBehavior,
    validationContextType
  );

  const validationErrors = getCollectionValidationErrors("JointAccountOwners");

  useEffect(() => {
    getJointAccountOwnerIds();
  }, [financialApplicationId]);

  const getJointAccountOwnerIds = async () => {
    setIsLoading(true);
    jointAccountOwnerApiClient
      .getJointAccountOwnerIds(financialApplicationId)
      .then((ids) => {
        setJointAccountOwnerIds(ids);
      })
      .catch((error) => {
        console.error(error);
        AppToasterV2.showFailedActionToaster("Failed to get joint owner IDs.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDeleteJointAccount = (jointAccountOwnerId: string) => {
    dialogStore.openConfirmDialog({
      dialogId: "delete-joint-account-" + jointAccountOwnerId,
      headerText: "Delete Joint Owner",
      content: "Are you sure you want to delete this joint owner?",
      confirmButtonText: "Delete",
      loadingProgressContent: "Delete",
      loadingSuccessContent: "Delete",
      color: "sunset-red",
      onConfirm: async () => {
        const result = {
          isSuccess: true,
          error: undefined,
        };

        try {
          await jointAccountOwnerApiClient.deleteJointAccountOwner(financialApplicationId, jointAccountOwnerId);
          setJointAccountOwnerIds(jointAccountOwnerIds.filter((id) => id !== jointAccountOwnerId));
          dialogStore.closeDialog("delete-joint-account-" + jointAccountOwnerId);
          AppToasterV2.showSuccessAppToaster("Joint owner deleted.");
          await refreshFinancialApplication();
        } catch (error: any) {
          console.error(error);
          result.isSuccess = false;
          result.error = error; 
        }

        return result;
      },
    });
  };

  const handleAddJointAccount = async () => {
    try {
      setIsCreating(true);
      const owner = await jointAccountOwnerApiClient.createJointAccountOwner(financialApplicationId);
      if (owner.id) {
        setJointAccountOwnerIds([owner.id, ...jointAccountOwnerIds]);
        AppToasterV2.showSuccessAppToaster("Joint owner added.");
        await refreshFinancialApplication();
      }
    } catch (error) {
      console.error(error);
      AppToasterV2.showFailedActionToaster("Failed to add joint owner.");
    } finally {
      setIsCreating(false);
    }
  };

  // Since we're now using the validationState from the financial application,
  // we don't need to make explicit validation calls
  const validateJointAccountOwners = useCallback(async (): Promise<boolean> => {
    const { isValid } = await validateFinancialApplicationFormSection();
    return isValid;
  }, [validateFinancialApplicationFormSection]);

  return {
    jointAccountOwnerIds,
    handleDeleteJointAccount,
    handleAddJointAccount,
    validateJointAccountOwners,
    isLoading,
    isCreating,
    validationErrors,
  };
};
